import { modifier } from 'ember-modifier'
import { assert } from '@ember/debug'

interface Signature {
  Args: {
    Named: unknown
    Positional: unknown[]
  }
  Element: HTMLElement
}

/**
 * Detect events that occur outside the given element, and call the given action.
 *
 * Note that if the event target is the element itself, that does not count as "outside".
 *
 * Example usage:
 * <div
 *  {{on "click" (fn this.setInside true)}}
 *  {{on-outside "click" (fn this.setInside false)}}
 * >
 *   {{if this.inside "inside!" "outside!"}}
 * </div>
 */
export default modifier<Signature>(function onOutside(element, positional = [], _named = {}) {
  const [eventName, callback] = positional

  // Similar to asserts for embers' {{on}} modifier
  const isValidEventName = typeof eventName === 'string' && eventName.length > 1
  const isValidCallback = typeof callback === 'function'
  assert(`on-outside: '${String(eventName)}' is not a valid event name. Provide a non-empty string`, isValidEventName)
  assert(`on-outside: '${String(callback)}' is not a valid callback. Provide a function.`, isValidCallback)

  const eventHandler = (event: Event, ...args: any[]) => {
    const isInside = element.contains(event.target as Nullable<Node>)
    if (!isInside) callback?.(event, ...args)
  }

  document.addEventListener(eventName, eventHandler)

  return () => {
    document.removeEventListener(eventName, eventHandler)
  }
})
